// AuthShell.jsx - Shared Voyager Pro split-screen layout for all auth pages
// (agency login, independent login, admin login). Left panel is branding/marketing
// (decorative globe, feature list, stats) - purely visual, no logic. Right panel is
// a slot for the actual auth form, which keeps its own state/handlers.
import React from 'react';

function GlobeDecoration() {
  const latitudes = [-150, -100, -50, 0, 50, 100, 150];
  const longitudes = [0, 30, 60, 90, 120, 150];
  return (
    <svg viewBox="0 0 500 500" fill="none" className="absolute" aria-hidden="true"
      style={{ width: 560, height: 560, top: '50%', right: -100, transform: 'translateY(-50%)', opacity: 0.055 }}>
      <circle cx="250" cy="250" r="240" stroke="white" strokeWidth="1" />
      {latitudes.map((y, i) => {
        const r = Math.sqrt(240 * 240 - y * y);
        return r > 0 ? (
          <ellipse key={i} cx="250" cy={250 + y} rx={r} ry={r * 0.28} stroke="white" strokeWidth="0.8" />
        ) : null;
      })}
      {longitudes.map((angle, i) => (
        <ellipse key={i} cx="250" cy="250" rx={240 * Math.sin((angle * Math.PI) / 180) || 8} ry="240"
          stroke="white" strokeWidth="0.8" transform={`rotate(${angle} 250 250)`} />
      ))}
      <path d="M 80 200 Q 200 100 380 180" stroke="white" strokeWidth="1.2" strokeDasharray="6 5" />
      <path d="M 120 320 Q 280 200 430 300" stroke="white" strokeWidth="1.2" strokeDasharray="6 5" />
      <path d="M 160 150 Q 300 260 420 220" stroke="white" strokeWidth="1" strokeDasharray="4 6" />
      {[[80, 200], [380, 180], [120, 320], [430, 300], [160, 150], [420, 220], [250, 80], [200, 380]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4" fill="white" />
      ))}
    </svg>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-3 group">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-105"
        style={{ backgroundColor: 'rgba(196,135,31,0.2)', color: '#C4871F' }}>
        {icon}
      </div>
      <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">{label}</span>
    </div>
  );
}

const FEATURES = [
  { label: 'AI-Powered Quote Builder', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg> },
  { label: 'Client & Lead Management', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
  { label: 'Group Booking Management', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg> },
  { label: 'Commission Tracking', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> },
  { label: 'Payment Links & Reminders', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg> },
  { label: 'Analytics & Goal Setting', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg> },
];

const STATS = [
  { value: '180+', label: 'Travel Agents' },
  { value: '$4.2M', label: 'Revenue Tracked' },
  { value: '12k+', label: 'Quotes Built' },
];

export function AuthLogo({ light = true, size = 'md' }) {
  const box = size === 'sm' ? 'w-9 h-9' : 'w-10 h-10';
  const icon = size === 'sm' ? 16 : 18;
  return (
    <div className="flex items-center gap-3">
      <div className={`${box} rounded-xl flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: 'var(--accent)' }}>
        <svg width={icon} height={icon} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      </div>
      <div>
        <div className={`${size === 'sm' ? 'text-lg' : 'text-xl'} font-bold ${light ? 'text-white' : 'text-[var(--foreground)]'} tracking-tight`} style={{ fontFamily: 'var(--font-display)' }}>
          Voyager Pro
        </div>
        {size !== 'sm' && <div className="text-[10px] font-semibold uppercase tracking-widest text-white/40">Travel Agent CRM</div>}
      </div>
    </div>
  );
}

/**
 * AuthShell - split-screen layout. `children` renders in the right-hand form
 * panel; `panelWidth` lets narrower/wider forms (e.g. registration) adjust.
 */
const AuthShell = ({ children, panelWidth = 'lg:w-[440px] xl:w-[480px]', footer }) => {
  return (
    <div className="voyager-app fixed inset-0 flex overflow-hidden">
      {/* Left branding panel */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #1A2B4A 0%, #0F1C2E 60%, #1A2B4A 100%)' }}>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div className="absolute" style={{
          width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(196,135,31,0.12) 0%, transparent 70%)',
          top: -100, left: -100,
        }} />
        <GlobeDecoration />

        <div className="relative z-10 flex flex-col h-full px-12 py-10">
          <AuthLogo light />

          <div className="flex-1 flex flex-col justify-center py-12">
            <div className="mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ backgroundColor: 'rgba(196,135,31,0.2)', color: '#C4871F' }}>
                Built for travel professionals
              </span>
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mt-4 mb-4" style={{ fontFamily: 'var(--font-display)' }}>
              Everything you need to<br />
              <span style={{ color: '#C4871F' }}>grow your travel</span><br />
              business
            </h1>
            <p className="text-base text-white/60 leading-relaxed mb-10 max-w-md">
              Voyager Pro brings together quotes, clients, groups, payments, and analytics in one elegant platform designed for independent agents and agencies alike.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map(f => <Feature key={f.label} {...f} />)}
            </div>
          </div>

          <div className="flex items-center gap-8 pt-6 border-t border-white/10">
            {STATS.map(s => (
              <div key={s.label}>
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>{s.value}</div>
                <div className="text-xs text-white/50 mt-0.5">{s.label}</div>
              </div>
            ))}
            <div className="flex-1 text-right">
              <div className="inline-flex items-center gap-2">
                {[0, 1, 2, 3, 4].map(i => (
                  <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill="#C4871F" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                ))}
                <span className="text-xs text-white/50 ml-1">5.0 avg rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className={`flex-1 lg:flex-none ${panelWidth} flex flex-col overflow-y-auto`} style={{ backgroundColor: 'var(--background)' }}>
        <div className="lg:hidden flex items-center gap-3 px-6 pt-8 pb-4">
          <AuthLogo light={false} size="sm" />
        </div>

        <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 py-8">
          <div className="w-full max-w-sm mx-auto">
            {children}
          </div>
        </div>

        <div className="px-8 py-5 text-center border-t border-[var(--border)]">
          {footer || (
            <>
              <p className="text-[11px] text-[var(--muted-foreground)]">© {new Date().getFullYear()} Voyager Pro</p>
              <div className="mt-1">
                <a href="/" className="text-[11px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline transition-colors">← Back to home</a>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes authFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Shared form-control styling/helpers reused by the login pages.
export const AUTH_INPUT = 'w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] focus:border-[var(--accent)] transition-all';

export function AuthField({ label, htmlFor, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={htmlFor} className="text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)]">{label}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}

export function AuthErrorBanner({ children }) {
  if (!children) return null;
  return (
    <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-medium text-red-700 bg-red-50 border border-red-200">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      {children}
    </div>
  );
}

export function AuthPrimaryButton({ loading, loadingLabel, children, ...props }) {
  return (
    <button
      className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
      style={{ backgroundColor: 'var(--primary)' }}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? (
        <>
          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
          {loadingLabel || 'Please wait…'}
        </>
      ) : children}
    </button>
  );
}

export function AuthPasswordInput({ value, onChange, showPassword, setShowPassword, ...props }) {
  return (
    <div className="relative">
      <input type={showPassword ? 'text' : 'password'} value={value} onChange={onChange}
        className={`${AUTH_INPUT} pr-10`} {...props} />
      <button type="button" onClick={() => setShowPassword(v => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors">
        {showPassword
          ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
          : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
        }
      </button>
    </div>
  );
}

export default AuthShell;
